/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import Papa from 'papaparse';
import {
  AdminEnrolment,
  AdminEnrolmentPerson,
  AdminEnrolmentProblem,
  AdminEnrolmentResult,
  JoinedVia,
} from '../models/playlist/admin-enrolment.model';
import { PillColor } from '../styleguide/components/pill/ptl-pill.component';
import { parseCsv } from './csv.helper';

export const PEOPLE_CSV_COLUMNS = ['email', 'first_name', 'last_name', 'external_id'] as const;

/** The most people one request may enrol. */
export const MAX_PEOPLE = 1000;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isEmailAddress(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

/** A file of one row, with the header the import reads. */
export function personCsv(person: AdminEnrolmentPerson): string {
  return Papa.unparse({
    fields: [...PEOPLE_CSV_COLUMNS],
    data: [[person.email, person.firstName ?? '', person.lastName ?? '', person.externalId ?? '']],
  });
}

export function peopleCsvTemplate(): string {
  return Papa.unparse({ fields: [...PEOPLE_CSV_COLUMNS], data: [] });
}

/**
 * The people in a file, by lower-cased address, for showing names next to the API's results. Read the way the import reads
 * it: a first row holding an address means there is no header, and no names.
 */
export function readPeopleCsv(text: string): Map<string, AdminEnrolmentPerson> {
  const records = parseCsv(text, ',').map((record) => record.fields.map((field) => field.trim()));
  const people = new Map<string, AdminEnrolmentPerson>();
  const [first, ...rest] = records;
  if (!first) {
    return people;
  }
  const addressColumn = first.findIndex(isEmailAddress);
  if (addressColumn >= 0) {
    records.forEach((fields) => addPerson(people, { email: fields[addressColumn] ?? '' }));
    return people;
  }
  const column = (name: string) => first.findIndex((field) => field.toLowerCase() === name);
  const [email, firstName, lastName, externalId] = PEOPLE_CSV_COLUMNS.map(column);
  if (email < 0) {
    return people;
  }
  rest.forEach((fields) =>
    addPerson(people, {
      email: fields[email] ?? '',
      firstName: cell(fields, firstName),
      lastName: cell(fields, lastName),
      externalId: cell(fields, externalId),
    }),
  );
  return people;
}

function cell(fields: string[], column: number): string | undefined {
  return column >= 0 && fields[column] ? fields[column] : undefined;
}

function addPerson(people: Map<string, AdminEnrolmentPerson>, person: AdminEnrolmentPerson): void {
  const key = person.email.toLowerCase();
  if (key && !people.has(key)) {
    people.set(key, person);
  }
}

export interface AdminEnrolmentSummary {
  total: number;
  enrolled: number;
  newAccounts: number;
  alreadyEnrolled: number;
  problems: number;
  outsideAudience: number;
  leavingWaitingList: number;
  /** Set when enrolling them goes over the seat limit. */
  seatsOver: { limit: number; taken: number } | null;
}

export function summarise(enrolment: AdminEnrolment): AdminEnrolmentSummary {
  const enrolled = enrolment.people.filter((person) => person.outcome === 'ENROLLED');
  const seatLimit = enrolment.seatLimit;
  return {
    total: enrolment.people.length,
    enrolled: enrolled.length,
    newAccounts: enrolled.filter((person) => person.newAccount).length,
    alreadyEnrolled: enrolment.people.filter((person) => person.outcome === 'ALREADY_ENROLLED').length,
    problems: enrolment.people.filter((person) => person.outcome === 'NOT_ENROLLED').length,
    outsideAudience: enrolled.filter((person) => person.outsideAudience).length,
    leavingWaitingList: enrolled.filter(leavesWaitingList).length,
    seatsOver:
      seatLimit !== null && seatLimit !== undefined && enrolment.seatsTaken > seatLimit
        ? { limit: seatLimit, taken: enrolment.seatsTaken }
        : null,
  };
}

function leavesWaitingList(person: AdminEnrolmentResult): boolean {
  return person.previousStatus === 'WAITLISTED' || person.previousStatus === 'SEAT_OFFERED';
}

export type AdminEnrolmentRowFilter = 'ALL' | 'ENROL' | 'NEEDS_A_LOOK' | 'SKIPPED';

export function matchesRowFilter(person: AdminEnrolmentResult, rowFilter: AdminEnrolmentRowFilter): boolean {
  switch (rowFilter) {
    case 'ENROL':
      return person.outcome === 'ENROLLED';
    case 'NEEDS_A_LOOK':
      return person.outcome === 'ENROLLED' && (person.outsideAudience || leavesWaitingList(person));
    case 'SKIPPED':
      return person.outcome !== 'ENROLLED';
    default:
      return true;
  }
}

export interface AdminEnrolmentTag {
  key: string;
  color: PillColor;
}

/** What enrolling the person does, as tags; a skipped person has a reason instead, from {@link skippedReasonKey}. */
export function rowTags(person: AdminEnrolmentResult): AdminEnrolmentTag[] {
  if (person.outcome !== 'ENROLLED') {
    return [];
  }
  const tags: AdminEnrolmentTag[] = [{ key: 'translations.adminEnrolment.tag.enrol', color: 'green' }];
  if (person.newAccount) {
    tags.push({ key: 'translations.adminEnrolment.tag.newAccount', color: 'violet' });
  }
  if (person.outsideAudience) {
    tags.push({ key: 'translations.adminEnrolment.tag.outsideAudience', color: 'amber' });
  }
  if (leavesWaitingList(person)) {
    tags.push({ key: 'translations.adminEnrolment.tag.leavesWaitingList', color: 'amber' });
  }
  if (person.previousStatus === 'PENDING_APPROVAL') {
    tags.push({ key: 'translations.adminEnrolment.tag.closesRequest', color: 'sky' });
  }
  if (person.previousStatus === 'INVITED') {
    tags.push({ key: 'translations.adminEnrolment.tag.closesInvitation', color: 'sky' });
  }
  return tags;
}

const PROBLEM_KEYS: Record<AdminEnrolmentProblem, string> = {
  USER_NOT_FOUND: 'translations.adminEnrolment.problem.userNotFound',
  INVALID_EMAIL: 'translations.adminEnrolment.problem.invalidEmail',
  MISSING_NAME: 'translations.adminEnrolment.problem.missingName',
  NOT_ALLOWED_TO_CREATE_ACCOUNT: 'translations.adminEnrolment.problem.notAllowedToCreateAccount',
  ACCOUNT_NOT_CREATED: 'translations.adminEnrolment.problem.accountNotCreated',
  DUPLICATE: 'translations.adminEnrolment.problem.duplicate',
  CHANGED: 'translations.adminEnrolment.problem.changed',
};

export function skippedReasonKey(person: AdminEnrolmentResult): string | null {
  if (person.outcome === 'ALREADY_ENROLLED') {
    return 'translations.adminEnrolment.problem.alreadyEnrolled';
  }
  if (person.outcome === 'NOT_ENROLLED') {
    return PROBLEM_KEYS[person.problem ?? 'USER_NOT_FOUND'];
  }
  return null;
}

/** The rows that weren't enrolled, with why, for the admin to fix and upload again. */
export function skippedRowsCsv(
  people: AdminEnrolmentResult[],
  file: Map<string, AdminEnrolmentPerson>,
  translate: (key: string) => string,
): string {
  const data = people
    .filter((person) => person.outcome !== 'ENROLLED')
    .map((person) => {
      const known = file.get((person.email ?? '').toLowerCase());
      return [
        person.email ?? '',
        known?.firstName ?? '',
        known?.lastName ?? '',
        known?.externalId ?? '',
        translate(skippedReasonKey(person) ?? ''),
      ];
    });
  return Papa.unparse({ fields: [...PEOPLE_CSV_COLUMNS, 'reason'], data });
}

const JOINED_VIA_KEYS: Record<JoinedVia, string> = {
  ADMIN: 'translations.adminEnrolment.joinedVia.admin',
  AUTO_ENROLMENT: 'translations.adminEnrolment.joinedVia.autoEnrolment',
  INVITATION: 'translations.adminEnrolment.joinedVia.invitation',
  REQUEST: 'translations.adminEnrolment.joinedVia.request',
  SELF_ENROLMENT: 'translations.adminEnrolment.joinedVia.selfEnrolment',
};

export function joinedViaKey(joinedVia: JoinedVia | null | undefined): string | null {
  return joinedVia ? JOINED_VIA_KEYS[joinedVia] : null;
}

/** Saves text as a CSV file, the way the members export does. */
export function downloadCsv(text: string, fileName: string): void {
  const link = document.createElement('a');
  link.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(text));
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
